import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, Handshake, Eye, EyeOff } from "lucide-react";

const PARTNER_ROLES = new Set([
  "franchise_partner", "professional_partner", "arn_partner",
  "insurance_partner", "education_partner", "study_abroad_partner",
  "admission_partner", "referral_partner", "external_partner",
  "student_client",
]);

/** Post-login redirect that honours password-change and account-type. */
function postLoginRedirect(user) {
  if (user?.password_must_change) return "/account/change-password";
  if (user?.role === "franchise_partner" || user?.role === "external_partner") {
    return "/portal/franchise";
  }
  if (user?.role === "student_client") return "/portal/student";
  // Professional-partner roles will get a dedicated portal in a later phase;
  // until then the RBAC-guarded Dashboard shows only their permitted records.
  return "/";
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("employee"); // "employee" | "partner"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      // If a partner-role user signs in from the employee tab (or vice versa)
      // we still let them through — auth is one system — but we route them
      // to the right home so they're not confused.
      if (tab === "employee" && u?.role && PARTNER_ROLES.has(u.role)) {
        toast.message("Partner account detected — taking you to your partner workspace.");
      } else if (tab === "partner" && u?.role && !PARTNER_ROLES.has(u.role)) {
        toast.message("Employee account detected — taking you to your employee workspace.");
      }
      nav(postLoginRedirect(u));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const isPartner = tab === "partner";

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-white">
      {/* Left visual panel — unchanged */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden miro-grain"
           style={{ background: "var(--miro-navy)" }}>
        <img
          src="https://images.unsplash.com/photo-1672750771479-5ea73e9439ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGJsdWUlMjBjb3Jwb3JhdGUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NDUzNjU2M3ww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-luminosity"
        />
        <div className="absolute inset-0"
             style={{ background: "radial-gradient(600px at 20% 30%, rgba(14,165,255,0.35), transparent 60%)" }}/>
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center p-1.5">
              <img src="/miro-logo.png" alt="MIRO" className="w-full h-full object-contain"/>
            </div>
            <div>
              <div className="font-display font-black text-2xl tracking-tight">MIRO One</div>
              <div className="text-[11px] uppercase tracking-widest text-white/60">Learn. Earn. Grow.</div>
            </div>
          </div>
          <div className="max-w-lg">
            <div className="miro-overline text-[var(--miro-blue)]">One Platform · Multiple Opportunities</div>
            <h2 className="font-display font-black text-4xl xl:text-5xl leading-[1.05] mt-3">
              One platform.<br/>Multiple opportunities.<br/>
              <span style={{ color: "var(--miro-blue)" }}>Transparent earnings.</span>
            </h2>
            <p className="text-white/70 mt-6 text-[15px] max-w-md">
              Powering MIRO Consultancy's employees, franchise partners and independent
              professionals — from first touch to lifetime value, in one auditable workspace.
            </p>
          </div>
          <div className="flex items-center gap-6 text-white/60 text-xs">
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[var(--miro-green)]"/> Employees · Partners · Franchise</div>
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[var(--miro-blue)]"/> Multi-product</div>
            <div className="flex items-center gap-2"><Sparkles size={13} className="text-[var(--miro-blue)]"/> AI-assisted</div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-white border border-[var(--miro-border)] flex items-center justify-center p-1">
              <img src="/miro-logo.png" alt="MIRO" className="w-full h-full object-contain"/>
            </div>
            <div className="font-display font-black text-xl">MIRO One</div>
          </div>

          {/* Tabs */}
          <div
            role="tablist"
            aria-label="Sign-in type"
            className="flex bg-[var(--miro-bg)] border border-[var(--miro-border)] rounded-lg p-1 mb-6"
          >
            <button
              role="tab"
              aria-selected={!isPartner}
              data-testid="login-tab-employee"
              onClick={() => setTab("employee")}
              className={`flex-1 h-9 rounded-md text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${!isPartner ? "bg-white text-[var(--miro-navy)] shadow-sm" : "text-[var(--miro-text-secondary)] hover:text-[var(--miro-navy)]"}`}
            >
              <ShieldCheck size={13}/> Employee Login
            </button>
            <button
              role="tab"
              aria-selected={isPartner}
              data-testid="login-tab-partner"
              onClick={() => setTab("partner")}
              className={`flex-1 h-9 rounded-md text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${isPartner ? "bg-white text-[var(--miro-navy)] shadow-sm" : "text-[var(--miro-text-secondary)] hover:text-[var(--miro-navy)]"}`}
            >
              <Handshake size={13}/> Partner Login
            </button>
          </div>

          <div className="miro-overline">{isPartner ? "MIRO One Partner Login" : "Welcome back"}</div>
          <h1 className="font-display font-black text-3xl tracking-tight text-[var(--miro-navy)] mt-1">
            {isPartner ? "MIRO One Partner Login" : "Sign in to MIRO One"}
          </h1>
          <p className="text-sm text-[var(--miro-text-secondary)] mt-2">
            {isPartner
              ? "Manage clients, submit business opportunities and track your earnings from one platform."
              : "Secure internal access for MIRO employees and authorised administrators."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid={isPartner ? "login-form-partner" : "login-form-employee"}>
            <div>
              <Label htmlFor="email">
                {isPartner ? "Registered email or mobile" : "Work email"}
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type={isPartner ? "text" : "email"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="mt-1 pr-10"
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--miro-text-muted)] hover:text-[var(--miro-navy)]"
                >
                  {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-11 font-semibold"
              style={{ background: "var(--miro-navy)" }}
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-[var(--miro-text-secondary)] flex items-center justify-between gap-2">
            <Link
              to="/forgot-password"
              data-testid="login-forgot-link"
              className="text-[var(--miro-blue)] hover:underline font-semibold"
            >
              Forgot password?
            </Link>
            {isPartner ? (
              <Link
                to="/partner/register"
                data-testid="login-partner-register-link"
                className="text-[var(--miro-text-secondary)] hover:text-[var(--miro-navy)] font-semibold"
              >
                Become a MIRO One Partner →
              </Link>
            ) : (
              <Link
                to="/request-access"
                data-testid="login-request-access-link"
                className="text-[var(--miro-text-secondary)] hover:text-[var(--miro-navy)] font-semibold"
              >
                Request Employee Access →
              </Link>
            )}
          </div>

          <div className="mt-8 rounded-lg border border-[var(--miro-border)] bg-[var(--miro-bg)] p-3 text-[11px] text-[var(--miro-text-secondary)] leading-relaxed">
            <div className="font-semibold text-[var(--miro-navy)] mb-1">🔒 Secure sign-in</div>
            <div>Accounts are locked for 15 minutes after 5 failed attempts.</div>
            {isPartner ? (
              <div>New partner? Tap <strong>Become a MIRO One Partner</strong> to submit an application. Access is granted after MIRO administrator approval.</div>
            ) : (
              <div>Employee accounts are created by HR Admin from an access request. Employees pay no membership fee.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
